import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useEventSharing, SharePlatform } from '@/hooks/useEventSharing';
import { ShareButton } from './ShareButton';
import { EventType } from '@/utils/eventUtils';

interface ShareDropdownProps {
  eventId: string;
  eventType: EventType;
  eventName: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export function ShareDropdown({
  eventId,
  eventType,
  eventName,
  location,
  startDate,
  endDate,
  description,
  variant = 'outline',
  size = 'sm',
  className = '',
}: ShareDropdownProps) {
  const {
    trackShare,
    generateEventUrl,
    generateShareText,
    shareToWhatsApp,
    shareToFacebook,
    shareToLinkedIn,
    shareToTwitter,
    copyToClipboard,
  } = useEventSharing();

  const handleShare = async (platform: SharePlatform) => {
    const eventUrl = generateEventUrl(eventId, eventType, platform);
    const shareText = generateShareText(eventName, location, startDate, endDate, description, eventUrl);

    await trackShare({
      eventId,
      eventType,
      platform,
    });

    switch (platform) {
      case 'whatsapp':
        shareToWhatsApp(shareText);
        break;
      case 'facebook':
        shareToFacebook(eventUrl, shareText);
        break;
      case 'linkedin':
        shareToLinkedIn(eventUrl);
        break;
      case 'twitter':
        shareToTwitter(eventUrl, shareText);
        break;
      case 'copy_link':
        await copyToClipboard(eventUrl, 'Link copied!');
        break;
      case 'copy_text':
        await copyToClipboard(shareText, 'Text copied!');
        break;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Share2 className="h-4 w-4 mr-1" />
          Share
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('whatsapp')}>
          <div className="flex items-center w-full">
            <span className="mr-2">💬</span> WhatsApp
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('facebook')}>
          <div className="flex items-center w-full">
            <span className="mr-2">📘</span> Facebook
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('linkedin')}>
          <div className="flex items-center w-full">
            <span className="mr-2">💼</span> LinkedIn
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('twitter')}>
          <div className="flex items-center w-full">
            <span className="mr-2">🐦</span> Twitter
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleShare('copy_link')}>
          <div className="flex items-center w-full">
            <span className="mr-2">🔗</span> Copy Link
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('copy_text')}>
          <div className="flex items-center w-full">
            <span className="mr-2">📝</span> Copy Text
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
